// Which page an addressed machine belongs on.
//
// A machine's kind can be changed on the Machines page while its screen is running:
// a hall board turned into a feedback tablet, a kiosk into a board. The screen learns
// its new kind at its next identify, and until now it went on drawing the old page
// with the new machine's settings. This says where it should be instead, so the page
// can move itself there without anybody walking to the machine.

export type ScreenPage = 'display' | 'kiosk' | 'feedback'

// The page each kind is drawn on. "board" is what a display was called for a few hours
// on 17 September 2026, and the server still accepts it, so it is drawn as a display.
const PAGE_OF_KIND: Record<string, ScreenPage> = {
  display: 'display',
  board: 'display',
  desk_display: 'display',
  kiosk: 'kiosk',
  feedback: 'feedback',
}

const SCREEN_PAGES: ReadonlySet<string> = new Set<ScreenPage>(['display', 'kiosk', 'feedback'])

/** The page a machine of this kind belongs on, when it is not the one it is on; null
 *  when it is, or when the kind is not a screen's. */
export function pageForUnit(
  on: string,
  unitKind: string | undefined,
  branchCode: string,
  unitCode: string,
): string | null {
  // Only a screen's page moves. An operator's page that happens to be told a kind
  // is not a machine, and must never be sent anywhere.
  if (!SCREEN_PAGES.has(on)) return null
  const page = unitKind ? PAGE_OF_KIND[unitKind] : undefined
  // A kind this page does not know is left where it is rather than guessed at: the
  // page it would be sent to might not draw it either, and a guess could bounce.
  if (!page || page === on) return null
  return `/${page}/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}`
}

/** The path a machine's screen opens at, as the server's screenPage builds it. */
export function screenPath(unitKind: string, branchCode: string, unitCode: string): string {
  return `/${PAGE_OF_KIND[unitKind] ?? 'display'}/${encodeURIComponent(branchCode)}/${encodeURIComponent(unitCode)}`
}
